import { api } from './client';
import type { Locale } from '../i18n';

export interface Solution {
  id: string;
  code: string;
  slug: string;
  locale: string;
  name: string;
  problem: string;
  outcome: string;
  approach: string;
  capabilities: string[];
  methodologyStageTags: string[];
  classification: string;
  relatedIndustrySlugs: string[];
  relatedPlatformModuleCodes: string[];
  externalUrl?: string | null;
}

export interface SolutionDetail extends Solution {
  relatedIndustries: Industry[];
  relatedPlatformModules: PlatformModule[];
}

export interface Industry {
  id: string;
  slug: string;
  locale: string;
  name: string;
  challenge: string;
  opportunity: string;
  solutionSummary: string;
  outcomeMetrics: unknown[];
}

export interface IndustryDetail extends Industry {
  relatedSolutions: Solution[];
}

export interface PlatformModule {
  id: string;
  code: string;
  locale: string;
  cluster: string;
  name: string;
  description: string;
  classification: string;
  demoAvailable: boolean;
}

export interface ResearchItem {
  id: string;
  slug: string;
  title: string;
  description: string;
  status: 'LIVE' | 'PROTOTYPE' | 'RESEARCH' | 'FUTURE';
  domain: string;
}

export interface EvidenceDocument {
  id: string;
  title: string;
  category: 'methodology' | 'security' | 'sovereignty' | 'team';
  body: unknown;
  evidenceSource: string;
}

export interface Page {
  id: string;
  slug: string;
  pageType: string;
  locale: string;
  contentBlocks: any;
}

export const contentApi = {
  solutions: (locale: Locale) => api.get<{ data: Solution[] }>(`/api/v1/solutions?locale=${locale}`).then((r) => r.data),
  solution: (code: string, locale: Locale) => api.get<{ data: SolutionDetail }>(`/api/v1/solutions/${code}?locale=${locale}`).then((r) => r.data),
  industries: (locale: Locale) => api.get<{ data: Industry[] }>(`/api/v1/industries?locale=${locale}`).then((r) => r.data),
  industry: (slug: string, locale: Locale) => api.get<{ data: IndustryDetail }>(`/api/v1/industries/${slug}?locale=${locale}`).then((r) => r.data),
  platformModules: (locale: Locale, cluster?: string) =>
    api.get<{ data: PlatformModule[] }>(`/api/v1/platform/modules?locale=${locale}${cluster ? `&cluster=${cluster}` : ''}`).then((r) => r.data),
  aiLabProjects: (locale: Locale, status?: string) =>
    api.get<{ data: ResearchItem[] }>(`/api/v1/ai-lab/projects?locale=${locale}${status ? `&status=${status}` : ''}`).then((r) => r.data),
  nationalCapabilities: (locale: Locale) => api.get<{ data: any[] }>(`/api/v1/national/capabilities?locale=${locale}`).then((r) => r.data),
  nationalMaturityModel: (locale: Locale) => api.get<{ data: string[] }>(`/api/v1/national/maturity-model?locale=${locale}`).then((r) => r.data),
  evidence: (locale: Locale, category?: string) =>
    api.get<{ data: EvidenceDocument[] }>(`/api/v1/trust/evidence?locale=${locale}${category ? `&category=${category}` : ''}`).then((r) => r.data),
  page: (slug: string, locale: Locale) => api.get<{ data: Page }>(`/api/v1/pages/${slug}?locale=${locale}`).then((r) => r.data),
  insights: (locale: Locale) => api.get<{ data: any[] }>(`/api/v1/insights?locale=${locale}`).then((r) => r.data),
  search: (q: string, locale: Locale) => api.get<{ data: any[]; total: number }>(`/api/v1/search?q=${encodeURIComponent(q)}&language=${locale}`),
};
